import React, { useState, useEffect, useRef } from 'react';
import styles from './styles/PatientDashboard.module.css';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import HumeSimpleEVI from '../components/HumeSimpleEVI';
import TestHumeConnection from '../components/TestHumeConnection';
import ProgressModal from './components/ProgressModal';
import ChatModal from './components/ChatModal';
import useAnalytics from '../hooks/useAnalytics';
import messagingService from '../services/MessagingService';
import patientService from '../services/PatientService';
import { startDashboardTour } from '../tours/patientDashboardTour';
import { startDashboardOptionsTour } from '../tours/dashboardOptionsTour';
import '../tours/tourStyles.css';
import GameTutorialCarousel from './components/GameTutorialCarousel';
import TermsAndConditions from '../components/TermsAndConditions';
import termsService from '../services/TermsService';

const PatientDashboard = () => {
  const [fontLoaded, setFontLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showTutorial, setShowTutorial] = useState(false);
  // Check localStorage for persistent HUME state
  const [isHumeActive, setIsHumeActive] = useState(
    localStorage.getItem('hume_voice_active') === 'true'
  );
  const [mendyAnimation, setMendyAnimation] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [userName, setUserName] = useState('Friend');
  const [assignedGames, setAssignedGames] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [therapyFrequency, setTherapyFrequency] = useState(null);
  // Check if terms have been accepted - will be loaded from database
  const [showTerms, setShowTerms] = useState(false);
  const [termsLoading, setTermsLoading] = useState(true);
  const eviRef = useRef(null);
  
  // No need for exercise data bridge with simple TTS
  const navigate = useNavigate();
  const { trackGameSelection, trackProgressView, trackLogout, trackLogin } = useAnalytics();
  
  // Game mapping between clinician dashboard IDs and patient dashboard
  const gameMapping = {
    'Apple Pick': { id: 'apple-pick', name: 'Apple Pick', icon: '/logos/apple_logo.svg', route: '/apple' },
    'Super Punch': { id: 'super-punch', name: 'Super Punch', icon: '/logos/super_punch_logo.svg', route: '/boxing' },
    'Hot Potato': { id: 'hot-potato', name: 'Hot Potato', icon: '/logos/hot_potato_logo.svg', route: '/potato' },
    'Tomato Dodge': { id: 'tomato', name: 'Tomato Dodge', icon: '/logos/tomato_logo.svg', route: '/tomato' },
    'Ocean Row': { id: 'ocean-row', name: 'Ocean Row', icon: '/logos/row_logo.svg', route: '/row' },
    'Sky Flight': { id: 'sky-flight', name: 'Sky Flight', icon: '/logos/sky_flight_logo.svg', route: '/sky' },
    'Plant Care': { id: 'plant-care', name: 'Plant Care', icon: '/logos/garden_logo.svg', route: '/garden' },
    'Berry Harvest': { id: 'berry-harvest', name: 'Berry Harvest', icon: '/logos/berry_picking_logo.svg', route: '/berry' }
  };

  // All available games (fallback)
  const allGames = Object.values(gameMapping);

  useEffect(() => {
    // Check terms acceptance from database
    const checkTermsAcceptance = async () => {
      const patientId = localStorage.getItem('patientId');
      if (patientId) {
        const accepted = await termsService.checkPatientTermsAcceptance(patientId);
        setShowTerms(!accepted);
      } else {
        // No patient ID, show terms by default
        setShowTerms(true);
      }
      setTermsLoading(false);
    };

    checkTermsAcceptance();

    // Get user name from localStorage and track login session
    const user = localStorage.getItem('user');
    let userName = 'Friend';
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.name) {
          userName = userData.name;
          setUserName(userName);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    // Track patient login session
    trackLogin(userName);
    console.log('📊 Patient session started:', userName);

    // Fetch assigned exercises for the patient
    const fetchAssignedExercises = async () => {
      try {
        const patientId = localStorage.getItem('patientId');
        console.log('Fetching exercises for patient:', patientId);

        if (patientId) {
          // Update patient's last active timestamp when they access the dashboard
          patientService.updatePatientLastActive(patientId);

          // Fetch therapy frequency
          const frequency = await patientService.getPatientTherapyFrequency(patientId);
          setTherapyFrequency(frequency);

          const assignments = await patientService.getPatientAssignments(patientId);
          console.log('Received assignments:', assignments);

          if (assignments && assignments.length > 0) {
            // Get unique exercise names to avoid duplicates
            const uniqueExerciseNames = [...new Set(assignments.map(a =>
              a.exercise_name || a.exerciseName
            ))];

            // Map unique exercise names to game objects
            const assignedGamesList = uniqueExerciseNames
              .map(exerciseName => gameMapping[exerciseName])
              .filter(game => game !== undefined);

            console.log('Unique exercises:', uniqueExerciseNames);
            console.log('Mapped assigned games:', assignedGamesList);
            setAssignedGames(assignedGamesList);
          } else {
            console.log('No assignments found');
            setAssignedGames([]);
          }
        } else {
          console.log('No patient ID found');
          setAssignedGames([]);
        }
      } catch (error) {
        console.error('Error fetching assigned exercises:', error);
        setAssignedGames([]);
      } finally {
        setLoadingAssignments(false);
      }
    };

    fetchAssignedExercises();

    // Check if font is loaded
    document.fonts.ready.then(() => {
      setFontLoaded(true);
    });
    
    // Apply background only to this component's page and remove when unmounted
    document.body.classList.add('dashboard-page');
    
    // Handle window resize for mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Load Mendy animation
    const loadMendyAnimation = async () => {
      try {
        const response = await fetch('/dashboard/mendy-animation.json');
        if (response.ok) {
          const animationData = await response.json();
          setMendyAnimation(animationData);
        } else {
          console.log('Mendy animation file not found, using static icon');
        }
      } catch (error) {
        console.log('Could not load Mendy animation:', error);
      }
    };
    
    loadMendyAnimation();
    
    return () => {
      // Track logout when component unmounts (browser close or navigation away)
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          const userName = userData.name || 'Friend';
          // Use beacon for reliable tracking on page unload
          trackLogout(userName, true);
          console.log('📊 Patient session ended (unmount):', userName);
        } catch (error) {
          console.error('Error tracking logout:', error);
        }
      }
      
      // Clean up by removing the class when component unmounts
      document.body.classList.remove('dashboard-page');
      window.removeEventListener('resize', handleResize);
    };
  }, [trackLogin, trackLogout]);

  // Unified tutorial flow: Dashboard tour (3 steps) → Game carousel (4 steps) → Options tour (2 steps) = 9 total
  // Only start tutorial AFTER terms are accepted
  useEffect(() => {
    const patientId = localStorage.getItem('patientId');
    
    // Wait for terms to be accepted before starting tutorial
    if (!termsLoading && !showTerms && !loadingAssignments && assignedGames.length > 0 && patientId) {
      const hasSeenGameTutorial = localStorage.getItem(`game_tutorial_seen_${patientId}`);
      
      // Callback to show game tutorial carousel after dashboard tour completes
      const showGameTutorialCarousel = () => {
        if (!hasSeenGameTutorial) {
          console.log('📚 Dashboard tour completed (Steps 1-3)');
          console.log('📚 Starting game tutorial carousel (Steps 4-7)');
          setTimeout(() => {
            setShowTutorial(true);
          }, 400); // Smooth transition
        }
      };
      
      // Step 1-3: Start dashboard tour (or skip if already seen)
      // This will call showGameTutorialCarousel() when complete
      console.log('✅ Terms accepted - starting tutorial flow');
      startDashboardTour(showGameTutorialCarousel);
    } else if (!termsLoading && showTerms) {
      console.log('⏸️ Tutorial paused - waiting for terms acceptance');
    }
  }, [termsLoading, showTerms, loadingAssignments, assignedGames]);

  const handleGameSelect = (game) => {
    // Track game selection for analytics
    trackGameSelection(game.name);
    
    // Clean up dashboard styles before navigating to game
    document.body.classList.remove('dashboard-page');
    
    // HUME can stay active for encouragement in games
    // setIsHumeActive(false); // Keep active for persistent encouragement
    
    // Add a small delay to let Hume EVI disconnect cleanly
    setTimeout(() => {
      navigate(game.route);
    }, 500);
  };

  const handleActivateHume = () => {
    const newState = !isHumeActive;
    setIsHumeActive(newState);
    
    if (newState) {
      console.log('Activating HUME voice for encouragement');
      // Store in localStorage to persist across games
      localStorage.setItem('hume_voice_active', 'true');
      
      // Give HUME time to connect before games can use it
      setTimeout(() => {
        console.log('HUME should be ready now');
        // Check connection status
        if (window.humeSimpleTTS) {
          const status = window.humeSimpleTTS.getStatus();
          console.log('HUME TTS Status:', status);
        }
      }, 2000);
    } else {
      console.log('Deactivating HUME voice');
      localStorage.removeItem('hume_voice_active');
    }
  };
  
  // Navigation function that will be passed to Hume EVI Tools service
  const navigateToGame = (route) => {
    console.log('🎮 Dashboard: Navigating to game route:', route);
    
    // HUME stays active for encouragement
    setTimeout(() => {
      navigate(route);
    }, 500);
  };

  const handleEVIFeedback = (feedback) => {
    console.log('EVI Feedback:', feedback);
  };

  const handleProgressClick = () => {
    // Track progress view for analytics
    trackProgressView();
    
    setIsProgressModalOpen(true);
  };

  const handleCloseProgressModal = () => {
    setIsProgressModalOpen(false);
  };

  const handleChatClick = () => {
    setIsChatModalOpen(true);
    // Initialize messaging service for real-time updates
    messagingService.init();
  };

  const handleCloseChatModal = () => {
    setIsChatModalOpen(false);
  };

  const handleLogout = () => {
    // Track user logout for analytics
    trackLogout();
    
    // Clear authentication data
    localStorage.removeItem('user');
    // Reload the page to trigger authentication check
    window.location.reload();
  };

  const handleTermsAccept = async () => {
    const patientId = localStorage.getItem('patientId');
    if (patientId) {
      await termsService.acceptPatientTerms(patientId);
      console.log('✅ Patient terms accepted and saved to database');
    }
    setShowTerms(false);
  };

  return (
    <div className={styles["dashboard-container"]}>
      <div className={styles["dashboard-header"]}>
        <img src="/auth/logo.svg" alt="Logo" className={styles["dashboard-logo"]} />
        <h1 className={`${styles["dashboard-greeting"]} annie-font`} style={{ color: "#6D4F79", fontSize: "64px" }}>Hello</h1>
      </div>

      <div className={styles["dashboard-options"]}>
        <div className={`${styles["option-box"]} dashboard-progress-option`} onClick={handleProgressClick}>
          <img src="/dashboard/progress.svg" alt="Progress" className={styles["option-icon"]} />
          <div className={styles["option-text"]}>Understand your progress</div>
        </div>
        <div className={`${styles["option-box"]} dashboard-chat-option`} onClick={handleChatClick}>
          <img src="/dashboard/mendy.svg" alt="Mendy" className={styles["option-icon"]} />
          <div className={styles["option-text"]}>Chat with Mendy</div>
        </div>
        <div className={styles["option-box"]} onClick={handleLogout}>
          <img src="/dashboard/settings.svg" alt="Logout" className={styles["option-icon"]} />
          <div className={styles["option-text"]}>Logout</div>
        </div>
      </div>

      <div className={styles["games-section"]}>
        <h2 className={styles["games-title"]}>
          {loadingAssignments
            ? 'loading your exercises...'
            : assignedGames.length > 0
              ? 'your recommended games for the day'
              : 'no exercises assigned yet'
          }
        </h2>

        <div className={`${styles["games-grid"]} games-grid`}>
          {loadingAssignments ? (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              Loading...
            </div>
          ) : assignedGames.length > 0 ? (
            assignedGames.map((game, index) => (
              <div
                key={game.id}
                className={`${styles["game-item"]} game-item`}
                onClick={() => handleGameSelect(game)}
              >
                <img src={game.icon} alt={game.name} className={styles["game-icon"]} />
              </div>
            ))
          ) : (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              <p>Please check with your clinician to get exercises assigned.</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles["dashboard-footer"]}>
        {therapyFrequency && (
          <p className={`${styles["therapy-frequency"]} therapy-frequency`}>
            Your clinician has suggested you do your exercises: {therapyFrequency}
          </p>
        )}
        <p className={styles["hipaa-notice"]}>As per HIPAA guidelines, you will be logged out after 30 minutes of inactivity</p>
      </div>

      {/* Progress Modal */}
      <ProgressModal 
        isOpen={isProgressModalOpen} 
        onClose={handleCloseProgressModal}
        patientId={localStorage.getItem('patientId')}
      />

      {/* Chat Modal */}
      <ChatModal
        isOpen={isChatModalOpen}
        onClose={handleCloseChatModal}
        userName={userName}
        patientId={localStorage.getItem('patientId')}
      />

      {/* Simple HUME Voice for Encouragement */}
      <HumeSimpleEVI 
        ref={eviRef}
        isActive={isHumeActive}
        autoConnect={false}
      />
      
      {/* Debug Panel - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ display: 'none' }}>
          <TestHumeConnection />
        </div>
      )}

      {/* Game Tutorial Carousel Widget */}
      {showTutorial && (
        <GameTutorialCarousel 
          onClose={() => setShowTutorial(false)}
        />
      )}

      {/* Terms and Conditions Modal */}
      {showTerms && (
        <TermsAndConditions onAccept={handleTermsAccept} />
      )}
    </div>
  );
};

export default PatientDashboard; 